/**
 * Email settings (ADR-180 in platform): how a tenant's emails to its contacts leave, the address they
 * come from, and where the replies arrive.
 *
 * An admin picks the provider and, for SendGrid, types the account's key. core-api checks the
 * addresses and the reply domain when they are saved, makes the trigger SendGrid's Inbound Parse
 * posts the replies to, and gives back its URL whole. The key is write-only: the console sends one
 * and never reads one. A check reads the SendGrid account and the reply domain's DNS and says what
 * is still wrong.
 *
 * The shapes mirror core-api's `EmailSettingsResponse`, `EmailSettingsUpdate` and `EmailCheck`. What
 * the form refuses here follows `EmailSettingsUpdate` and `email_settings.save`, so the usual
 * mistakes are said beside their field and not as a 422. Pure, for `test/email-settings.test.ts`.
 */

export type EmailProvider = "off" | "sandbox" | "sendgrid";

/** The trigger the settings made for the replies, as core-api shows it. */
export interface EmailHook {
  trigger_id: number;
  name: string;
  enabled: boolean;
  /** The whole URL SendGrid posts to, secret included. Null when the trigger can no longer take it. */
  url: string | null;
  /** The URL without its host or secret: `/hooks/t/<key>/email`. */
  path: string;
  last_received_at: string | null;
  /** Why the trigger no longer does its work, after a change on the Triggers page. */
  problem: string | null;
}

export interface EmailSettings {
  tenant_id: number;
  provider: EmailProvider | string;
  /** Whether a key is kept. The key itself is never in an answer. */
  sendgrid_api_key_set: boolean;
  from_address: string | null;
  from_name: string | null;
  /** The subdomain each thread's reply address is at (`reply+<token>@<reply domain>`). */
  reply_domain: string | null;
  /** The only addresses an email may go to when the list is not empty: an address, or `@domain`. */
  allowed_recipients: string[];
  /** This deployment refuses to send through SendGrid with an empty list (staging). */
  allowlist_required: boolean;
  daily_limit: number;
  sent_today: number;
  public_url: string | null;
  incoming: EmailHook | null;
  checked_at: string | null;
  updated_at: string | null;
}

/** What `PUT /integrations/email` takes: every setting, each time. The key only when one was typed. */
export interface EmailSettingsInput {
  provider: EmailProvider;
  sendgrid_api_key?: string;
  from_address: string | null;
  from_name: string | null;
  reply_domain: string | null;
  allowed_recipients: string[];
  daily_limit: number;
  public_url: string | null;
  /** Superadmin only. */
  tenant_id?: number;
}

export interface EmailCheckItem {
  code: string;
  ok: boolean;
  detail: string;
}

export interface EmailCheck {
  ok: boolean;
  checked_at: string;
  items: EmailCheckItem[];
}

/** The form: every field as the text in its input. */
export interface EmailValues {
  provider: EmailProvider;
  /** Typed now. Empty keeps the key already kept. */
  apiKey: string;
  fromAddress: string;
  fromName: string;
  replyDomain: string;
  /** The allowed list as typed: one address or `@domain` a line. */
  allowed: string;
  publicUrl: string;
  dailyLimit: string;
}

export const EMAIL_PROVIDERS: { value: EmailProvider; label: string; description: string }[] = [
  { value: "off", label: "Off", description: "Nothing is sent, and the Email button is not on a record." },
  { value: "sandbox", label: "Sandbox", description: "Every email is taken and none is sent. For a test stack." },
  { value: "sendgrid", label: "SendGrid", description: "Emails leave through your SendGrid account." },
];

/** Which parts of the page belong on it for the provider that is picked. */
export interface EmailSections {
  /** The SendGrid API key. */
  key: boolean;
  /** The addresses, the public address, the allowed list and the daily limit. */
  addresses: boolean;
  /** The URL SendGrid posts the replies to, which saving made. */
  url: boolean;
  /** The check of the SendGrid account and the reply domain. */
  check: boolean;
  /** One line under the picker: what the provider does, or what saving it will bring. */
  note: string | null;
}

/**
 * What the page shows for the provider picked in the form. Only what that provider has a use for:
 *
 * - Off: the picker and nothing else. Nothing is sent, so there is nothing to type or check.
 * - Sandbox: the addresses, the reply URL, the allowed list and the limit. Every thread still gets
 *   its reply address and a reply can be posted by hand, so all of it is real; no key, and no check
 *   of an account nothing reaches.
 * - SendGrid: everything. The URL and the check come once it is saved: both need the saved account.
 *
 * The parts under the form are about the settings as they are saved. So while the provider picked
 * is not the saved one yet, none of them shows, and the note says what saving brings.
 */
export function emailSections(picked: EmailProvider, settings: EmailSettings): EmailSections {
  const live = picked === settings.provider;
  const kept = settings.sendgrid_api_key_set;
  let note: string | null;
  if (picked === "sendgrid") {
    note = live
      ? null
      : "Save, and the next steps show here: the URL to paste into SendGrid's Inbound Parse, and a check of the account.";
  } else if (picked === "sandbox") {
    note = live
      ? "An email sent from the console is checked and kept in its thread as usual, and reads as sent. Nothing reaches an inbox."
      : "Save, and every email is taken and none is sent. Each thread still gets its reply address.";
  } else if (live) {
    note = kept
      ? "A send is refused, saying email is switched off. The SendGrid key is kept for when you switch back."
      : "A send is refused, saying email is switched off. Pick SendGrid to set it up.";
  } else {
    note = kept
      ? "Save to switch email off. The SendGrid key and the reply URL are kept for when you switch back."
      : "Save to switch email off.";
  }
  return {
    key: picked === "sendgrid",
    addresses: picked !== "off",
    url: live && picked !== "off" && settings.incoming !== null,
    check: live && picked === "sendgrid",
    note,
  };
}

export const DEFAULT_DAILY_LIMIT = 1_000;
export const MAX_DAILY_LIMIT = 1_000_000;
/** Entries on the allowed list: `email_settings.MAX_ALLOWED`. */
export const MAX_ALLOWED = 200;
export const MAX_API_KEY_CHARS = 200;
export const MAX_FROM_NAME_CHARS = 100;
/** The hosts a public address may name with plain http: this computer, for a local stack. */
const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "host.docker.internal"]);
const LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
// One address: something, one @, a domain with a dot. core-api's email_validator decides the rest.
const ADDRESS = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// A line break in the from name would end the From header and start another.
const CONTROL = /[\u0000-\u001f\u007f-\u009f\u2028\u2029\u200b-\u200f\u202a-\u202e\u2060-\u2064\ufeff]/;
/** `emails.PUBLIC_MAIL_DOMAINS`: a whole one of these on the list would let a tester email anyone. */
const PUBLIC_MAIL_DOMAINS = new Set([
  "gmail.com", "googlemail.com", "yahoo.com", "outlook.com", "hotmail.com", "live.com", "msn.com",
  "icloud.com", "me.com", "aol.com", "proton.me", "protonmail.com", "gmx.com",
]);

function isProvider(value: string): value is EmailProvider {
  return value === "off" || value === "sandbox" || value === "sendgrid";
}

/** The form for the settings core-api holds. `fallbackPublicUrl` fills an address nobody has set:
 *  the SMS page's guess (`defaultPublicUrl`), the console's own address when it is public. */
export function emailValues(settings: EmailSettings, fallbackPublicUrl = ""): EmailValues {
  return {
    provider: isProvider(settings.provider) ? settings.provider : "off",
    apiKey: "",
    fromAddress: settings.from_address ?? "",
    fromName: settings.from_name ?? "",
    replyDomain: settings.reply_domain ?? "",
    allowed: settings.allowed_recipients.join("\n"),
    publicUrl: settings.public_url ?? fallbackPublicUrl,
    dailyLimit: String(settings.daily_limit),
  };
}

/** Why a name cannot be a reply domain, or an allowed list's `@domain`, or null. As core-api's
 *  `reply_domain_problem`: a lowercase host name of two parts or more. */
export function replyDomainProblem(domain: string): string | null {
  if (!domain || domain.includes("@")) return "A domain is the part after the @, like reply.example.com.";
  const labels = domain.split(".");
  if (domain.length > 253 || labels.length < 2 || !labels.every((label) => LABEL.test(label))) {
    return "A domain is a host name, like reply.example.com: lowercase letters, digits and hyphens, in parts joined by dots.";
  }
  return null;
}

/** Why an address cannot be the one SendGrid reaches this system at, or null. As core-api's
 *  `email_settings.public_url_problem`: the SMS page's rule, except that plain http is taken for
 *  this computer, where a reply is posted by hand. */
export function emailPublicUrlProblem(url: string): string | null {
  let parts: URL;
  try {
    parts = new URL(url);
  } catch {
    return "The public address is a URL, like https://console.example.com/api.";
  }
  const local = parts.protocol === "http:" && LOCAL_HOSTS.has(parts.hostname.toLowerCase());
  if (parts.protocol !== "https:" && !local) {
    return "The public address must start with https://, or http:// for this computer (localhost). A trigger's secret travels in it.";
  }
  if (!parts.hostname) return "The public address needs a host name, like https://console.example.com/api.";
  if (parts.username || parts.password) {
    return "The public address takes no user name or password. The secret is added to it.";
  }
  if (parts.search || parts.hash || /[\s?#]/.test(url)) {
    return "The public address is the part before /hooks: no ? and no # in it.";
  }
  return null;
}

/**
 * The allowed list as typed, one entry a line (commas and spaces split too), as core-api keeps it:
 * lowercase, an address or `@domain`, each once, in the order typed. `problem` names the first entry
 * that is neither, in core-api's words.
 */
export function parseAllowed(text: string): { entries: string[]; problem: string | null } {
  const entries: string[] = [];
  let problem: string | null = null;
  for (const raw of text.split(/[\s,;]+/)) {
    const entry = raw.trim().toLowerCase();
    if (!entry) continue;
    const bad = entry.startsWith("@")
      ? replyDomainProblem(entry.slice(1)) !== null
      : !ADDRESS.test(entry) || entry.length > 254;
    if (bad) {
      problem ??= `${entry} is not ${entry.startsWith("@") ? "a domain" : "an address"}: an entry is an address, or @ and a domain, like @example.com.`;
      continue;
    }
    if (!entries.includes(entry)) entries.push(entry);
  }
  if (!problem && entries.length > MAX_ALLOWED) {
    problem = `The allowed list holds at most ${MAX_ALLOWED} entries.`;
  }
  return { entries, problem };
}

function limitOf(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d{1,7}$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return value <= MAX_DAILY_LIMIT ? value : null;
}

function domainOf(address: string): string {
  return address.slice(address.lastIndexOf("@") + 1);
}

/** Why the form cannot be saved, and the field it is about (null: the form as a whole). */
export interface EmailProblem {
  field: keyof EmailValues | null;
  message: string;
}

/**
 * Why the form cannot be saved as it stands, or null: the first thing wrong, with the field to say it
 * beside. Each field is checked for what it is, then what the provider picked needs.
 *
 * With Off picked the fields are not on the page and are not sent (`emailPayload`), so nothing typed
 * in them can stand in the way of switching email off.
 *
 * While this deployment emails only an allowed list (`allowlist_required`, staging), SendGrid cannot
 * be saved with an empty one, and a whole public mail domain is refused, as core-api refuses both.
 * The sandbox sends nothing, so it is never held to the list.
 */
export function emailProblem(values: EmailValues, settings: EmailSettings): EmailProblem | null {
  if (values.provider === "off") return null;
  const key = values.apiKey.trim();
  const from = values.fromAddress.trim().toLowerCase();
  const name = values.fromName.trim();
  const domain = values.replyDomain.trim().toLowerCase().replace(/\.$/, "");
  const address = values.publicUrl.trim().replace(/\/+$/, "");
  const sendgrid = values.provider === "sendgrid";

  if (sendgrid && (key.length > MAX_API_KEY_CHARS || /\s/.test(key))) {
    return { field: "apiKey", message: "A SendGrid API key has no spaces in it, and is at most 200 characters." };
  }
  if (!from) return { field: "fromAddress", message: "Email needs the address it is sent from." };
  if (!ADDRESS.test(from) || from.length > 254) {
    return { field: "fromAddress", message: "The from address is an email address, like help@example.com." };
  }
  if (name.length > MAX_FROM_NAME_CHARS) {
    return { field: "fromName", message: `The from name is at most ${MAX_FROM_NAME_CHARS} characters.` };
  }
  if (CONTROL.test(name)) {
    return { field: "fromName", message: "The from name is one line, with no control characters." };
  }
  if (!domain) return { field: "replyDomain", message: "Email needs the domain replies come back to." };
  const domainProblem = replyDomainProblem(domain);
  if (domainProblem) return { field: "replyDomain", message: domainProblem };
  if (domainOf(from) === domain) {
    return {
      field: "replyDomain",
      message: "The from address cannot be at the reply domain: replies would come back as new mail.",
    };
  }
  if (limitOf(values.dailyLimit) === null) {
    return {
      field: "dailyLimit",
      message: `The daily limit is a whole number from 0 to ${MAX_DAILY_LIMIT.toLocaleString("en-US")}. 0 is no limit.`,
    };
  }
  if (address) {
    const urlProblem = emailPublicUrlProblem(address);
    if (urlProblem) return { field: "publicUrl", message: urlProblem };
  }
  const allowed = parseAllowed(values.allowed);
  if (allowed.problem) return { field: "allowed", message: allowed.problem };
  if (settings.allowlist_required) {
    const open = allowed.entries.find((entry) => entry.startsWith("@") && PUBLIC_MAIL_DOMAINS.has(entry.slice(1)));
    if (open) {
      return {
        field: "allowed",
        message: `While this system emails only an allowed list, list whole addresses, not ${open}.`,
      };
    }
    if (sendgrid && allowed.entries.length === 0) {
      return { field: "allowed", message: "This system emails only an allowed list: add the testers' addresses first." };
    }
  }
  if (sendgrid && !key && !settings.sendgrid_api_key_set) {
    return { field: "apiKey", message: "SendGrid needs an API key." };
  }
  if (sendgrid && !address) {
    return {
      field: "publicUrl",
      message: "SendGrid needs the public address of this system: where it posts the replies.",
    };
  }
  return null;
}

/**
 * What `PUT /integrations/email` is sent for the form. core-api takes every setting each time, so:
 *
 * - With the sandbox or SendGrid picked, the fields as typed, and the key only when one was typed
 *   (and only for SendGrid, the one provider that has a key on the page).
 * - With Off picked, the provider, and everything else as core-api already holds it. The fields are
 *   not on the page then, so a save that only switches email off saves nothing typed in them.
 */
export function emailPayload(values: EmailValues, settings: EmailSettings): EmailSettingsInput {
  if (values.provider === "off") {
    return {
      provider: "off",
      from_address: settings.from_address,
      from_name: settings.from_name,
      reply_domain: settings.reply_domain,
      allowed_recipients: settings.allowed_recipients,
      daily_limit: settings.daily_limit,
      public_url: settings.public_url,
    };
  }
  const key = values.apiKey.trim();
  const address = values.publicUrl.trim().replace(/\/+$/, "");
  return {
    provider: values.provider,
    ...(values.provider === "sendgrid" && key ? { sendgrid_api_key: key } : {}),
    from_address: values.fromAddress.trim().toLowerCase() || null,
    from_name: values.fromName.trim() || null,
    reply_domain: values.replyDomain.trim().toLowerCase().replace(/\.$/, "") || null,
    allowed_recipients: parseAllowed(values.allowed).entries,
    daily_limit: limitOf(values.dailyLimit) ?? DEFAULT_DAILY_LIMIT,
    public_url: address || null,
  };
}

/**
 * Whether saving the form would change anything. With Off picked, only the provider: the fields are
 * not on the page and are not sent. Otherwise any field that differs from what was loaded, the
 * allowed list as core-api would keep it, and a typed key always.
 */
export function emailChanged(values: EmailValues, loaded: EmailValues): boolean {
  if (values.provider === "off") return values.provider !== loaded.provider;
  const same = (a: string, b: string) => a.trim() === b.trim();
  const list = (text: string) => parseAllowed(text).entries.join("\n");
  return (
    values.provider !== loaded.provider ||
    (values.provider === "sendgrid" && values.apiKey.trim() !== "") ||
    !same(values.fromAddress, loaded.fromAddress) ||
    !same(values.fromName, loaded.fromName) ||
    !same(values.replyDomain, loaded.replyDomain) ||
    list(values.allowed) !== list(loaded.allowed) ||
    values.publicUrl.trim().replace(/\/+$/, "") !== loaded.publicUrl.trim().replace(/\/+$/, "") ||
    !same(values.dailyLimit, loaded.dailyLimit)
  );
}
